import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./BonusManagementPage.css";

const defaultForm = {
  signupBonus: "",
  level1Reward: "",
  level2Reward: "",
  level3Reward: "",
  level4Reward: "",
  level5Reward: "",
  brokeragePoolPercentage: "",
  brokerageLevel1Percentage: "",
  brokerageLevel2Percentage: "",
  brokerageLevel3Percentage: "",
  brokerageLevel4Percentage: "",
  brokerageLevel5Percentage: "",
  adminAlertEmail: "",
  adminAlertMobile: "",
  currencyShortName: "",
  socialLinkOne: "",
  socialLinkTwo: "",
  socialLinkThree: "",
};

const toNumberOrZero = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const BonusManagementPage = () => {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (message.text) setMessage({ type: "", text: "" });
  };

  const fetchOtherSettings = async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await AdminService.getOtherSettings();
      if (res?.success && res?.data) {
        setForm({
          signupBonus: String(res.data.signupBonus ?? ""),
          level1Reward: String(res.data.level1Reward ?? ""),
          level2Reward: String(res.data.level2Reward ?? ""),
          level3Reward: String(res.data.level3Reward ?? ""),
          level4Reward: String(res.data.level4Reward ?? ""),
          level5Reward: String(res.data.level5Reward ?? ""),
          brokeragePoolPercentage: String(res.data.brokeragePoolPercentage ?? ""),
          brokerageLevel1Percentage: String(res.data.brokerageLevel1Percentage ?? ""),
          brokerageLevel2Percentage: String(res.data.brokerageLevel2Percentage ?? ""),
          brokerageLevel3Percentage: String(res.data.brokerageLevel3Percentage ?? ""),
          brokerageLevel4Percentage: String(res.data.brokerageLevel4Percentage ?? ""),
          brokerageLevel5Percentage: String(res.data.brokerageLevel5Percentage ?? ""),
          adminAlertEmail: String(res.data.adminAlertEmail ?? ""),
          adminAlertMobile: String(res.data.adminAlertMobile ?? ""),
          currencyShortName: String(res.data.currencyShortName ?? ""),
          socialLinkOne: String(res.data.socialLinkOne ?? ""),
          socialLinkTwo: String(res.data.socialLinkTwo ?? ""),
          socialLinkThree: String(res.data.socialLinkThree ?? ""),
        });
      } else {
        setMessage({ type: "error", text: res?.message || "Failed to fetch settings." });
      }
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || err?.message || "Failed to fetch settings." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOtherSettings();
  }, []);

  const handleSave = async () => {
    const payload = {
      signupBonus: toNumberOrZero(form.signupBonus),
      level1Reward: toNumberOrZero(form.level1Reward),
      level2Reward: toNumberOrZero(form.level2Reward),
      level3Reward: toNumberOrZero(form.level3Reward),
      level4Reward: toNumberOrZero(form.level4Reward),
      level5Reward: toNumberOrZero(form.level5Reward),
      brokeragePoolPercentage: toNumberOrZero(form.brokeragePoolPercentage),
      brokerageLevel1Percentage: toNumberOrZero(form.brokerageLevel1Percentage),
      brokerageLevel2Percentage: toNumberOrZero(form.brokerageLevel2Percentage),
      brokerageLevel3Percentage: toNumberOrZero(form.brokerageLevel3Percentage),
      brokerageLevel4Percentage: toNumberOrZero(form.brokerageLevel4Percentage),
      brokerageLevel5Percentage: toNumberOrZero(form.brokerageLevel5Percentage),
      adminAlertEmail: String(form.adminAlertEmail || "").trim(),
      adminAlertMobile: String(form.adminAlertMobile || "").trim(),
      currencyShortName: String(form.currencyShortName || "").trim(),
      socialLinkOne: String(form.socialLinkOne || "").trim(),
      socialLinkTwo: String(form.socialLinkTwo || "").trim(),
      socialLinkThree: String(form.socialLinkThree || "").trim(),
    };

    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await AdminService.updateOtherSettings(payload);
      if (res?.success) {
        setMessage({ type: "success", text: res?.message || "Settings updated successfully." });
        if (res?.data) {
          setForm({
            signupBonus: String(res.data.signupBonus ?? payload.signupBonus),
            level1Reward: String(res.data.level1Reward ?? payload.level1Reward),
            level2Reward: String(res.data.level2Reward ?? payload.level2Reward),
            level3Reward: String(res.data.level3Reward ?? payload.level3Reward),
            level4Reward: String(res.data.level4Reward ?? payload.level4Reward),
            level5Reward: String(res.data.level5Reward ?? payload.level5Reward),
            brokeragePoolPercentage: String(res.data.brokeragePoolPercentage ?? payload.brokeragePoolPercentage),
            brokerageLevel1Percentage: String(res.data.brokerageLevel1Percentage ?? payload.brokerageLevel1Percentage),
            brokerageLevel2Percentage: String(res.data.brokerageLevel2Percentage ?? payload.brokerageLevel2Percentage),
            brokerageLevel3Percentage: String(res.data.brokerageLevel3Percentage ?? payload.brokerageLevel3Percentage),
            brokerageLevel4Percentage: String(res.data.brokerageLevel4Percentage ?? payload.brokerageLevel4Percentage),
            brokerageLevel5Percentage: String(res.data.brokerageLevel5Percentage ?? payload.brokerageLevel5Percentage),
            adminAlertEmail: String(res.data.adminAlertEmail ?? payload.adminAlertEmail),
            adminAlertMobile: String(res.data.adminAlertMobile ?? payload.adminAlertMobile),
            currencyShortName: String(res.data.currencyShortName ?? payload.currencyShortName),
            socialLinkOne: String(res.data.socialLinkOne ?? payload.socialLinkOne),
            socialLinkTwo: String(res.data.socialLinkTwo ?? payload.socialLinkTwo),
            socialLinkThree: String(res.data.socialLinkThree ?? payload.socialLinkThree),
          });
        }
      } else {
        setMessage({ type: "error", text: res?.message || "Failed to update settings." });
      }
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || err?.message || "Failed to update settings." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage bonus-management-page">
      <section className="dashboard-hero bonus-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Bonus Management</span>
          </p>
          <h1 className="dashboard-hero__title">Bonus Management</h1>
          <p className="dashboard-hero__subtitle">Manage signup and referral rewards</p>
        </div>
        <div className="bonus-hero__icon">
          <i className="fas fa-gift" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="bonus-card">
          <h2 className="bonus-card__title">
            <i className="fas fa-coins" /> Other Settings
          </h2>

          {loading ? (
            <div className="bonus-loading"><i className="fas fa-spinner fa-spin" /> Loading settings...</div>
          ) : (
            <div className="bonus-form-grid">
              <div className="bonus-form-group">
                <label>Signup Bonus</label>
                <input
                  type="number"
                  min="0"
                  value={form.signupBonus}
                  onChange={(e) => updateField("signupBonus", e.target.value)}
                  placeholder="Enter signup bonus"
                />
              </div>
              <div className="bonus-form-group">
                <label>Level 1 Reward</label>
                <input
                  type="number"
                  min="0"
                  value={form.level1Reward}
                  onChange={(e) => updateField("level1Reward", e.target.value)}
                  placeholder="Enter level 1 reward"
                />
              </div>
              <div className="bonus-form-group">
                <label>Level 2 Reward</label>
                <input
                  type="number"
                  min="0"
                  value={form.level2Reward}
                  onChange={(e) => updateField("level2Reward", e.target.value)}
                  placeholder="Enter level 2 reward"
                />
              </div>
              <div className="bonus-form-group">
                <label>Level 3 Reward</label>
                <input
                  type="number"
                  min="0"
                  value={form.level3Reward}
                  onChange={(e) => updateField("level3Reward", e.target.value)}
                  placeholder="Enter level 3 reward"
                />
              </div>
              <div className="bonus-form-group">
                <label>Level 4 Reward</label>
                <input
                  type="number"
                  min="0"
                  value={form.level4Reward}
                  onChange={(e) => updateField("level4Reward", e.target.value)}
                  placeholder="Enter level 4 reward"
                />
              </div>
              <div className="bonus-form-group">
                <label>Level 5 Reward</label>
                <input
                  type="number"
                  min="0"
                  value={form.level5Reward}
                  onChange={(e) => updateField("level5Reward", e.target.value)}
                  placeholder="Enter level 5 reward"
                />
              </div>
              <div className="bonus-form-group">
                <label>Brokerage Pool Percentage</label>
                <input
                  type="number"
                  min="0"
                  value={form.brokeragePoolPercentage}
                  onChange={(e) => updateField("brokeragePoolPercentage", e.target.value)}
                  placeholder="Enter brokerage pool percentage"
                />
              </div>
              <div className="bonus-form-group">
                <label>Brokerage Level 1 Percentage</label>
                <input
                  type="number"
                  min="0"
                  value={form.brokerageLevel1Percentage}
                  onChange={(e) => updateField("brokerageLevel1Percentage", e.target.value)}
                  placeholder="Enter brokerage level 1 percentage"
                />
              </div>
              <div className="bonus-form-group">
                <label>Brokerage Level 2 Percentage</label>
                <input
                  type="number"
                  min="0"
                  value={form.brokerageLevel2Percentage}
                  onChange={(e) => updateField("brokerageLevel2Percentage", e.target.value)}
                  placeholder="Enter brokerage level 2 percentage"
                />
              </div>
              <div className="bonus-form-group">
                <label>Brokerage Level 3 Percentage</label>
                <input
                  type="number"
                  min="0"
                  value={form.brokerageLevel3Percentage}
                  onChange={(e) => updateField("brokerageLevel3Percentage", e.target.value)}
                  placeholder="Enter brokerage level 3 percentage"
                />
              </div>
              <div className="bonus-form-group">
                <label>Brokerage Level 4 Percentage</label>
                <input
                  type="number"
                  min="0"
                  value={form.brokerageLevel4Percentage}
                  onChange={(e) => updateField("brokerageLevel4Percentage", e.target.value)}
                  placeholder="Enter brokerage level 4 percentage"
                />
              </div>
              <div className="bonus-form-group">
                <label>Brokerage Level 5 Percentage</label>
                <input
                  type="number"
                  min="0"
                  value={form.brokerageLevel5Percentage}
                  onChange={(e) => updateField("brokerageLevel5Percentage", e.target.value)}
                  placeholder="Enter brokerage level 5 percentage"
                />
              </div>
              <div className="bonus-form-group">
                <label>Admin Alert Email</label>
                <input
                  type="email"
                  value={form.adminAlertEmail}
                  onChange={(e) => updateField("adminAlertEmail", e.target.value)}
                  placeholder="alerts@zillion.com"
                />
              </div>
              <div className="bonus-form-group">
                <label>Admin Alert Mobile</label>
                <input
                  type="text"
                  value={form.adminAlertMobile}
                  onChange={(e) => updateField("adminAlertMobile", e.target.value)}
                  placeholder="919876543210"
                />
              </div>
              <div className="bonus-form-group bonus-form-group--full">
                <label>Currency Short Name</label>
                <input
                  type="text"
                  value={form.currencyShortName}
                  onChange={(e) => updateField("currencyShortName", e.target.value.toUpperCase())}
                  placeholder="USDT"
                />
              </div>
              <div className="bonus-form-group bonus-form-group--full">
                <label>Social Link One</label>
                <input
                  type="text"
                  value={form.socialLinkOne}
                  onChange={(e) => updateField("socialLinkOne", e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div className="bonus-form-group bonus-form-group--full">
                <label>Social Link Two</label>
                <input
                  type="text"
                  value={form.socialLinkTwo}
                  onChange={(e) => updateField("socialLinkTwo", e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div className="bonus-form-group bonus-form-group--full">
                <label>Social Link Three</label>
                <input
                  type="text"
                  value={form.socialLinkThree}
                  onChange={(e) => updateField("socialLinkThree", e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>
          )}

          {message.text ? (
            <p className={`bonus-message bonus-message--${message.type || "error"}`}>{message.text}</p>
          ) : null}

          <div className="bonus-actions">
            <button
              type="button"
              className="bonus-btn-save"
              onClick={handleSave}
              disabled={loading || saving}
            >
              <i className={`fas ${saving ? "fa-spinner fa-spin" : "fa-save"}`} /> {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </section>
      </div>
    </main>
  );
};

export default BonusManagementPage;
